"use client";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Empty, Spin } from "antd";

import DoctorInfoCard from "../DoctorInfoCard/DoctorInfoCard";
import { NotMedilinkerPopup } from "../MapSection/MapSection";

import "./DoctorListView.css";

const DEFAULT_AVATAR =
  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

/* =========================================================
   HELPERS
========================================================= */

/* Titres à ignorer pour ne pas prendre "D" de "Dr." */
const TITLE_REGEX = /^(dr|pr|prof|docteur|doctor)\.?$/i;

/* Types qui identifient un cabinet / une clinique */
const CLINIC_TYPE_REGEX =
  /(cabinet|clinic|clinique|hospital|hopital|hôpital|centre|center|medical_center|health|dentist|dentiste)/i;

/* Mots dans le nom qui identifient un cabinet / une clinique */
const CLINIC_NAME_REGEX =
  /(cabinet|clinique|clinic|polyclinique|centre|center|hôpital|hopital|hospital)/i;

/**
 * Détermine si l'élément est un cabinet / une clinique.
 * ⚠️ Adaptez les champs (type, category, ...) à la structure
 * réelle de vos données si nécessaire.
 */
const isClinic = (doctor) => {
  if (!doctor) return false;

  if (doctor.isClinic || doctor.isCabinet) return true;

  const typeValue = String(
    doctor.type || doctor.category || doctor.placeType || ""
  );

  if (typeValue && CLINIC_TYPE_REGEX.test(typeValue)) return true;

  if (CLINIC_NAME_REGEX.test(String(doctor.name || "").trim())) return true;

  return false;
};

/**
 * Détecte les images qui ne sont pas de vraies photos
 * (avatar par défaut, icônes Google Places, etc.)
 */
const isPlaceholderImage = (src) => {
  if (!src) return true;

  if (src === DEFAULT_AVATAR) return true;

  const value = String(src).toLowerCase();

  return (
    value.includes("maps.gstatic.com/mapfiles") ||
    value.includes("place_api/icons") ||
    value.includes("/icons/v1/") ||
    value.endsWith(".svg")
  );
};

/**
 * Initiales : 1ère lettre du 1er mot + 1ère lettre du 2ème mot.
 * Ex: "Dr. Ahmed Ben Ali" -> "AB"
 *     "Sami"              -> "SA"
 */
const getInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((part) => !TITLE_REGEX.test(part));

  if (!parts.length) return "?";

  if (parts.length >= 2) {
    const first = Array.from(parts[0])[0] || "";
    const second = Array.from(parts[1])[0] || "";

    return `${first}${second}`.toUpperCase();
  }

  return Array.from(parts[0]).slice(0, 2).join("").toUpperCase();
};

/* =========================================================
   ICONS
========================================================= */

/* Icône par défaut pour les cabinets (lucide "hospital") */
const HospitalIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="dlv-hospital-icon"
    aria-hidden="true"
  >
    <path d="M12 7v4" />
    <path d="M14 21v-3a2 2 0 0 0-4 0v3" />
    <path d="M14 9h-4" />
    <path d="M18 11h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h2" />
    <path d="M18 21V5a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16" />
  </svg>
);

const PhoneIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M18.333 14.167v2.5a1.667 1.667 0 0 1-1.817 1.666 16.49 16.49 0 0 1-7.192-2.559 16.25 16.25 0 0 1-5-5 16.49 16.49 0 0 1-2.559-7.225A1.667 1.667 0 0 1 3.425 1.667h2.5a1.667 1.667 0 0 1 1.667 1.435c.106.8.303 1.586.585 2.34a1.667 1.667 0 0 1-.375 1.759L6.658 8.342a13.333 13.333 0 0 0 5 5l1.141-1.142a1.667 1.667 0 0 1 1.759-.375c.754.282 1.54.479 2.34.585a1.667 1.667 0 0 1 1.435 1.692z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PinIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M16.667 8.333c0 4.16-4.617 8.494-6.167 9.832a.833.833 0 0 1-1 0c-1.55-1.338-6.167-5.671-6.167-9.832a6.667 6.667 0 0 1 13.334 0z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M10 10.833a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* =========================================================
   AVATAR
   - cabinet  -> toujours l'icône hôpital (image ignorée)
   - médecin  -> vraie photo, sinon initiales
========================================================= */

const DoctorAvatar = ({ name, src, clinic = false }) => {
  const [imageFailed, setImageFailed] = useState(false);

  /* Si la source change, on réessaie de charger l'image */
  useEffect(() => {
    setImageFailed(false);
  }, [src]);

  /* ---------- CABINET : icône hôpital, on ignore l'image ---------- */
  if (clinic) {
    return (
      <div className="dlv-avatar-wrapper">
        <div
          className="dlv-avatar dlv-avatar-fallback dlv-avatar-fallback--clinic"
          aria-label={name || "Clinic"}
        >
          <HospitalIcon />
        </div>
      </div>
    );
  }

  /* ---------- MÉDECIN : photo réelle ou initiales ---------- */
  const hasRealImage = !isPlaceholderImage(src) && !imageFailed;

  return (
    <div className="dlv-avatar-wrapper">
      {hasRealImage ? (
        <img
          className="dlv-avatar"
          src={src}
          alt={name || "Doctor"}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <div
          className="dlv-avatar dlv-avatar-fallback"
          aria-label={name || "Doctor"}
        >
          {getInitials(name)}
        </div>
      )}
    </div>
  );
};

/* =========================================================
   COMPONENT
========================================================= */

const DoctorListView = ({
  doctors = [],
  loading,
  error,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /*
   * Médecin externe sélectionné dans la liste.
   * => affiche DoctorInfoCard.
   */
  const [selectedInfoDoctor, setSelectedInfoDoctor] = useState(null);

  /*
   * Deuxième étape :
   * clic "Not on MediLink" depuis DoctorInfoCard.
   */
  const [notMedilinkerDoctor, setNotMedilinkerDoctor] = useState(null);

  /* =======================================================
     CALENDAR / BOOK
  ======================================================= */

  const handleBook = (doctor) => {
    /*
     * MediLink :
     * comportement normal -> schedule.
     */
    if (doctor?.isMedilinker) {
      const id = doctor?.medilinkerAccountId || doctor?.id;

      if (id) {
        navigate(`/doctor-details/${id}?tab=schedule`);
      }

      return;
    }

    /*
     * EXTERNAL :
     * on affiche d'abord DoctorInfoCard.
     */
    setSelectedInfoDoctor(doctor);
  };

  /* =======================================================
     CLICK ROW
  ======================================================= */

  const handleDoctorClick = (doctor) => {
    /*
     * On veut cette interaction seulement
     * pour les médecins externes.
     */
    if (doctor?.isMedilinker) {
      return;
    }

    setSelectedInfoDoctor(doctor);
  };

  /* =======================================================
     CLICK FROM INFO CARD
  ======================================================= */

  const handleExternalAppointment = (doctor) => {
    /*
     * Fermer DoctorInfoCard
     */
    setSelectedInfoDoctor(null);

    /*
     * Puis montrer le popup :
     * "This doctor is not on MediLink"
     */
    setNotMedilinkerDoctor(doctor);
  };

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="dlv-state">
        <Spin tip={t("doctorFinder.messages.loadingNearby")} />
      </div>
    );
  }

  /* =======================================================
     ERROR
  ======================================================= */

  if (error) {
    return (
      <div className="dlv-state dlv-state--error">
        <p>{error}</p>
      </div>
    );
  }

  /* =======================================================
     EMPTY
  ======================================================= */

  if (!doctors.length) {
    return (
      <div className="dlv-state">
        <Empty
          description={t(
            "doctorFinder.messages.noResults",
            "No doctors found"
          )}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  /* =======================================================
     LIST
  ======================================================= */

  return (
    <div className="dlv-list-shell">
      {/* ===================================================
          LIST
      =================================================== */}

      <div className="dlv-list">
        {doctors.map((doctor, index) => {
          const key = doctor?.id || `${doctor?.name}-${index}`;

          const isExternal = !doctor?.isMedilinker;

          const isSelected = selectedInfoDoctor?.id === doctor?.id;

          return (
            <article
              key={key}
              className={[
                "dlv-item",
                isExternal ? "dlv-item--external" : "dlv-item--medilinker",
                isSelected ? "dlv-item--selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => handleDoctorClick(doctor)}
            >
              {/* ===========================================
                  AVATAR (photo, icône cabinet ou initiales)
              =========================================== */}

              <DoctorAvatar
                name={doctor?.name}
                src={doctor?.avatar}
                clinic={isClinic(doctor)}
              />

              {/* ===========================================
                  NAME + SPECIALTY
              =========================================== */}

              <div className="dlv-main">
                <p className="dlv-name" title={doctor?.name || ""}>
                  {doctor?.name ||
                    t("doctorFinder.card.doctor", "Doctor")}
                </p>

                <p className="dlv-specialty" title={doctor?.specialty || ""}>
                  {doctor?.specialty ||
                    t(
                      "doctorFinder.card.specialty",
                      "General practitioner"
                    )}
                </p>
              </div>

              {/* ===========================================
                  PHONE + ADDRESS
              =========================================== */}

              <div className="dlv-meta-stack">
                {doctor?.phone && (
                  <div className="dlv-meta">
                    <PhoneIcon />

                    <span title={doctor.phone}>{doctor.phone}</span>
                  </div>
                )}

                {doctor?.address && (
                  <div className="dlv-meta dlv-meta--address">
                    <PinIcon />

                    <span title={doctor.address}>{doctor.address}</span>
                  </div>
                )}

                {!doctor?.phone && !doctor?.address && (
                  <span className="dlv-empty-meta">—</span>
                )}
              </div>

              {/* ===========================================
                  CALENDAR
              =========================================== */}

              <div className="dlv-action-column">
                <button
                  type="button"
                  className="dlv-book-btn"
                  onClick={(event) => {
                    /*
                     * Important :
                     * sinon le click remonte vers article.
                     */
                    event.stopPropagation();

                    handleBook(doctor);
                  }}
                  aria-label={
                    doctor?.isMedilinker
                      ? t(
                          "doctorFinder.card.bookAppointment",
                          "Book appointment"
                        )
                      : t(
                          "doctorFinder.card.viewDoctorInfo",
                          "View doctor information"
                        )
                  }
                  title={
                    doctor?.isMedilinker
                      ? t(
                          "doctorFinder.card.bookAppointment",
                          "Book appointment"
                        )
                      : t(
                          "doctorFinder.card.viewDoctorInfo",
                          "View doctor information"
                        )
                  }
                >
                  <CalendarIcon />
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {/* ===================================================
          DOCTOR INFO CARD FOR EXTERNAL DOCTOR
      =================================================== */}

      {selectedInfoDoctor && (
        <>
          {/* Overlay léger */}
          <button
            type="button"
            className="dlv-info-backdrop"
            aria-label={t("common.close", "Close")}
            onClick={() => setSelectedInfoDoctor(null)}
          />

          <div className="dlv-info-card-wrapper">
            <DoctorInfoCard
              doctor={selectedInfoDoctor}
              isMedilinker={false}
              showClose
              onClose={() => setSelectedInfoDoctor(null)}
              onAppointmentClick={handleExternalAppointment}
            />
          </div>
        </>
      )}

      {/* ===================================================
          NOT MEDILINK POPUP
      =================================================== */}

      {notMedilinkerDoctor && (
        <NotMedilinkerPopup
          doctor={notMedilinkerDoctor}
          onClose={() => setNotMedilinkerDoctor(null)}
        />
      )}
    </div>
  );
};

export default DoctorListView;